import React, { useEffect } from 'react';

const LoadBalancing = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Load Balancing in Distributed Systems - Complete Guide 2024';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master load balancing techniques for scalable systems. Learn round-robin, weighted, least connections, and advanced traffic routing strategies.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master load balancing techniques for scalable systems. Learn round-robin, weighted, least connections, and advanced traffic routing strategies.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Load Balancing in Distributed Systems</h1>
        <p style={styles.subtitle}>
          Smart traffic routing across servers for optimal performance and high availability
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>What is Load Balancing?</h2>
          <p style={styles.paragraph}>
            Load balancing is the process of distributing incoming network traffic across multiple servers to ensure 
            no single server becomes overwhelmed. It improves application responsiveness, increases availability, and 
            enables horizontal scaling by adding more servers to handle increased load.
          </p>
          <p style={styles.paragraph}>
            A load balancer acts as a reverse proxy, sitting between clients and servers, intelligently routing 
            requests based on various algorithms and server health. This ensures optimal resource utilization and 
            provides fault tolerance by redirecting traffic away from failed servers.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Types of Load Balancers</h2>
          
          <h3 style={styles.heading}>Layer 4 (Transport Layer) Load Balancing</h3>
          <p style={styles.paragraph}>
            Layer 4 load balancers operate at the transport layer, making routing decisions based on IP addresses 
            and port numbers. They don't inspect the actual content of requests, making them fast and efficient.
          </p>
          <pre style={styles.codeBlock}>
{`Layer 4 Load Balancing:

Client Request → Load Balancer → Server
     ↓              ↓              ↓
   IP:Port    Routing Decision   IP:Port
             (TCP/UDP level)

Characteristics:
✓ High performance (no content inspection)
✓ Protocol agnostic (TCP, UDP)
✓ Lower latency
✓ Simple configuration

Use Cases:
- Database connection pooling
- TCP/UDP service distribution
- High-throughput applications
- Protocol-agnostic routing`}
          </pre>

          <h3 style={styles.heading}>Layer 7 (Application Layer) Load Balancing</h3>
          <p style={styles.paragraph}>
            Layer 7 load balancers operate at the application layer, making intelligent routing decisions based on 
            HTTP headers, URLs, cookies, and other application-specific data.
          </p>
          <pre style={styles.codeBlock}>
{`Layer 7 Load Balancing:

HTTP Request → Load Balancer → Server
     ↓              ↓              ↓
  URL/Headers  Content Inspection  Application
              Advanced Routing

Capabilities:
✓ Content-based routing
✓ SSL termination
✓ Request modification
✓ Advanced health checks
✓ Session persistence

Use Cases:
- Microservices routing
- A/B testing
- API gateway functionality
- Content-based distribution`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Load Balancing Algorithms</h2>
          
          <h3 style={styles.heading}>Round Robin</h3>
          <p style={styles.paragraph}>
            Round Robin distributes requests sequentially across available servers. It's simple and works well when 
            all servers have similar capacity and requests have similar processing requirements.
          </p>
          <pre style={styles.codeBlock}>
{`Round Robin Implementation:

class RoundRobinLoadBalancer:
    def __init__(self, servers):
        self.servers = servers
        self.current_index = 0
    
    def get_server(self):
        if not self.servers:
            return None
        
        server = self.servers[self.current_index]
        self.current_index = (self.current_index + 1) % len(self.servers)
        return server

# Example:
servers = ['server1', 'server2', 'server3']
lb = RoundRobinLoadBalancer(servers)

# Request distribution:
# Request 1 → server1
# Request 2 → server2  
# Request 3 → server3
# Request 4 → server1 (cycles back)

Pros:
✓ Simple to implement
✓ Even distribution over time
✓ No server state required

Cons:
✗ Doesn't consider server capacity
✗ Doesn't account for request complexity
✗ Poor performance with heterogeneous servers`}
          </pre>

          <h3 style={styles.heading}>Weighted Round Robin</h3>
          <p style={styles.paragraph}>
            Weighted Round Robin assigns different weights to servers based on their capacity, allowing more powerful 
            servers to handle more requests.
          </p>
          <pre style={styles.codeBlock}>
{`Weighted Round Robin Implementation:

class WeightedRoundRobinLoadBalancer:
    def __init__(self, servers_with_weights):
        # servers_with_weights: [('server1', 3), ('server2', 2), ('server3', 1)]
        self.servers = []
        for server, weight in servers_with_weights:
            self.servers.extend([server] * weight)
        self.current_index = 0
    
    def get_server(self):
        if not self.servers:
            return None
        
        server = self.servers[self.current_index]
        self.current_index = (self.current_index + 1) % len(self.servers)
        return server

# Example with weights:
servers = [('server1', 3), ('server2', 2), ('server3', 1)]
lb = WeightedRoundRobinLoadBalancer(servers)

# Distribution pattern:
# server1, server1, server1, server2, server2, server3
# (server1 gets 50%, server2 gets 33%, server3 gets 17%)

Use Cases:
✓ Heterogeneous server capacities
✓ Different server performance levels
✓ Gradual traffic migration
✓ Blue-green deployments`}
          </pre>

          <h3 style={styles.heading}>Least Connections</h3>
          <p style={styles.paragraph}>
            Least Connections routes new requests to the server with the fewest active connections, which works well 
            for applications with varying request processing times.
          </p>
          <pre style={styles.codeBlock}>
{`Least Connections Implementation:

class LeastConnectionsLoadBalancer:
    def __init__(self, servers):
        self.servers = {server: 0 for server in servers}  # server: active_connections
    
    def get_server(self):
        if not self.servers:
            return None
        
        # Find server with minimum connections
        min_connections = min(self.servers.values())
        candidates = [server for server, connections in self.servers.items() 
                     if connections == min_connections]
        
        # If multiple servers have same min connections, use round robin
        return candidates[0]  # Or implement tie-breaking logic
    
    def on_request_start(self, server):
        """Called when request starts processing"""
        if server in self.servers:
            self.servers[server] += 1
    
    def on_request_complete(self, server):
        """Called when request completes"""
        if server in self.servers:
            self.servers[server] = max(0, self.servers[server] - 1)

# Example usage:
lb = LeastConnectionsLoadBalancer(['server1', 'server2', 'server3'])

# Request flow:
server = lb.get_server()  # Returns server with least connections
lb.on_request_start(server)  # Increment connection count
# ... process request ...
lb.on_request_complete(server)  # Decrement connection count

Best For:
✓ Long-running requests
✓ Database connections
✓ WebSocket connections
✓ Varying request processing times`}
          </pre>

          <h3 style={styles.heading}>Least Response Time</h3>
          <p style={styles.paragraph}>
            Least Response Time considers both the number of active connections and the average response time of each 
            server, routing requests to the server that can respond fastest.
          </p>
          <pre style={styles.codeBlock}>
{`Least Response Time Implementation:

import time
from collections import deque

class LeastResponseTimeLoadBalancer:
    def __init__(self, servers):
        self.servers = {}
        for server in servers:
            self.servers[server] = {
                'active_connections': 0,
                'response_times': deque(maxlen=100),  # Last 100 response times
                'total_requests': 0
            }
    
    def get_server(self):
        if not self.servers:
            return None
        
        best_server = None
        best_score = float('inf')
        
        for server, stats in self.servers.items():
            # Calculate average response time
            if stats['response_times']:
                avg_response_time = sum(stats['response_times']) / len(stats['response_times'])
            else:
                avg_response_time = 0
            
            # Score = (active_connections * weight) + avg_response_time
            score = stats['active_connections'] * 0.5 + avg_response_time
            
            if score < best_score:
                best_score = score
                best_server = server
        
        return best_server
    
    def on_request_start(self, server):
        if server in self.servers:
            self.servers[server]['active_connections'] += 1
            return time.time()  # Return start time
    
    def on_request_complete(self, server, start_time):
        if server in self.servers:
            self.servers[server]['active_connections'] -= 1
            response_time = time.time() - start_time
            self.servers[server]['response_times'].append(response_time)
            self.servers[server]['total_requests'] += 1

Advantages:
✓ Considers both load and performance
✓ Adapts to server performance changes
✓ Better for varying server capabilities
✓ Self-optimizing based on actual performance`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Advanced Load Balancing Features</h2>
          
          <h3 style={styles.heading}>Health Checks</h3>
          <p style={styles.paragraph}>
            Health checks ensure that traffic is only routed to healthy servers by regularly monitoring server status 
            and removing failed servers from the rotation.
          </p>
          <pre style={styles.codeBlock}>
{`Health Check Implementation:

import asyncio
import aiohttp
from enum import Enum

class HealthStatus(Enum):
    HEALTHY = "healthy"
    UNHEALTHY = "unhealthy"
    UNKNOWN = "unknown"

class HealthCheckLoadBalancer:
    def __init__(self, servers, health_check_interval=30):
        self.servers = {server: HealthStatus.UNKNOWN for server in servers}
        self.health_check_interval = health_check_interval
        self.health_check_url = "/health"
        
        # Start health check background task
        asyncio.create_task(self._health_check_loop())
    
    async def _health_check_loop(self):
        """Continuously monitor server health"""
        while True:
            await self._check_all_servers()
            await asyncio.sleep(self.health_check_interval)
    
    async def _check_all_servers(self):
        """Check health of all servers"""
        tasks = []
        for server in self.servers.keys():
            tasks.append(self._check_server_health(server))
        
        await asyncio.gather(*tasks, return_exceptions=True)
    
    async def _check_server_health(self, server):
        """Check individual server health"""
        try:
            async with aiohttp.ClientSession() as session:
                url = f"http://{server}{self.health_check_url}"
                async with session.get(url, timeout=5) as response:
                    if response.status == 200:
                        self.servers[server] = HealthStatus.HEALTHY
                    else:
                        self.servers[server] = HealthStatus.UNHEALTHY
        except Exception as e:
            print(f"Health check failed for {server}: {e}")
            self.servers[server] = HealthStatus.UNHEALTHY
    
    def get_healthy_servers(self):
        """Get list of healthy servers"""
        return [server for server, status in self.servers.items() 
                if status == HealthStatus.HEALTHY]
    
    def get_server(self):
        """Get server using round robin among healthy servers"""
        healthy_servers = self.get_healthy_servers()
        if not healthy_servers:
            return None
        
        # Use round robin among healthy servers
        return healthy_servers[0]  # Simplified for example

Health Check Types:
1. HTTP/HTTPS checks (GET /health)
2. TCP connection checks
3. Custom application checks
4. Database connectivity checks
5. Dependency service checks`}
          </pre>

          <h3 style={styles.heading}>Session Persistence (Sticky Sessions)</h3>
          <p style={styles.paragraph}>
            Session persistence ensures that requests from the same client are always routed to the same server, 
            which is important for applications that store session state locally.
          </p>
          <pre style={styles.codeBlock}>
{`Session Persistence Implementation:

import hashlib

class StickySessionLoadBalancer:
    def __init__(self, servers):
        self.servers = servers
        self.session_map = {}  # session_id -> server
    
    def get_server_by_session(self, session_id):
        """Route based on session ID"""
        if session_id in self.session_map:
            # Check if assigned server is still available
            server = self.session_map[session_id]
            if server in self.servers:
                return server
            else:
                # Server no longer available, reassign
                del self.session_map[session_id]
        
        # Assign new server based on session hash
        server_index = hash(session_id) % len(self.servers)
        server = self.servers[server_index]
        self.session_map[session_id] = server
        return server
    
    def get_server_by_client_ip(self, client_ip):
        """Route based on client IP"""
        # Use consistent hashing based on IP
        server_index = int(hashlib.md5(client_ip.encode()).hexdigest(), 16) % len(self.servers)
        return self.servers[server_index]
    
    def get_server_by_cookie(self, request_headers):
        """Route based on cookie value"""
        cookie_header = request_headers.get('Cookie', '')
        
        # Extract session cookie
        for cookie in cookie_header.split(';'):
            if cookie.strip().startswith('SESSIONID='):
                session_id = cookie.split('=')[1].strip()
                return self.get_server_by_session(session_id)
        
        # No session cookie, use round robin
        return self.servers[0]  # Simplified

Sticky Session Methods:
1. Cookie-based (JSESSIONID, custom cookies)
2. IP-based (source IP hashing)
3. URL parameter-based
4. HTTP header-based
5. SSL session ID-based

Considerations:
✓ Maintains session state
✓ Better user experience
✗ Uneven load distribution
✗ Reduced fault tolerance
✗ Scaling challenges`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Load Balancer Deployment Patterns</h2>
          
          <h3 style={styles.heading}>Single Load Balancer</h3>
          <p style={styles.paragraph}>
            The simplest deployment with one load balancer handling all traffic. Easy to implement but creates a 
            single point of failure.
          </p>
          <pre style={styles.codeBlock}>
{`Single Load Balancer:

    Internet
        ↓
   Load Balancer
        ↓
  ┌─────┼─────┐
  ▼     ▼     ▼
Server1 Server2 Server3

Pros:
✓ Simple architecture
✓ Easy to configure
✓ Lower cost

Cons:
✗ Single point of failure
✗ Limited scalability
✗ Bottleneck for high traffic`}
          </pre>

          <h3 style={styles.heading}>High Availability Load Balancers</h3>
          <p style={styles.paragraph}>
            Deploy multiple load balancers with failover capability to eliminate single points of failure.
          </p>
          <pre style={styles.codeBlock}>
{`HA Load Balancer Setup:

    Internet
        ↓
   DNS Round Robin
    ↓         ↓
LB1 (Active) LB2 (Standby)
    ↓         ↓
  ┌─────┼─────┐
  ▼     ▼     ▼
Server1 Server2 Server3

Implementation Options:
1. Active-Passive (one active, one standby)
2. Active-Active (both handling traffic)
3. DNS-based failover
4. Virtual IP (VIP) with heartbeat
5. Cloud provider managed (AWS ELB, GCP Load Balancer)`}
          </pre>

          <h3 style={styles.heading}>Multi-Tier Load Balancing</h3>
          <p style={styles.paragraph}>
            Use multiple layers of load balancers for different tiers of your application architecture.
          </p>
          <pre style={styles.codeBlock}>
{`Multi-Tier Architecture:

    Internet
        ↓
  L7 Load Balancer (Application)
        ↓
  ┌─────┼─────┐
  ▼     ▼     ▼
 Web1  Web2  Web3
        ↓
  L4 Load Balancer (Database)
        ↓
  ┌─────┼─────┐
  ▼     ▼     ▼
 DB1   DB2   DB3

Benefits:
✓ Separation of concerns
✓ Specialized optimization per tier
✓ Better fault isolation
✓ Independent scaling

Use Cases:
- Web tier: HTTP load balancing
- App tier: Service mesh routing
- Database tier: Connection pooling
- Cache tier: Consistent hashing`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Examples</h2>
          
          <h3 style={styles.heading}>Netflix's Zuul</h3>
          <p style={styles.paragraph}>
            Netflix uses Zuul as an edge service that provides dynamic routing, monitoring, resiliency, and security. 
            It acts as a gateway for all requests from devices and websites to Netflix's backend services.
          </p>

          <h3 style={styles.heading}>Google's Maglev</h3>
          <p style={styles.paragraph}>
            Google's Maglev is a distributed software load balancer that provides consistent hashing and fast failover. 
            It can handle millions of queries per second and provides equal cost multipath (ECMP) routing.
          </p>

          <h3 style={styles.heading}>AWS Application Load Balancer</h3>
          <p style={styles.paragraph}>
            AWS ALB provides Layer 7 load balancing with advanced routing features including host-based routing, 
            path-based routing, and integration with AWS services like Auto Scaling and CloudWatch.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Performance Optimization</h2>
          
          <h3 style={styles.heading}>Connection Pooling</h3>
          <p style={styles.paragraph}>
            Implement connection pooling to reduce the overhead of establishing new connections for each request:
          </p>
          <pre style={styles.codeBlock}>
{`Connection Pool Implementation:

class ConnectionPool:
    def __init__(self, server, max_connections=100):
        self.server = server
        self.max_connections = max_connections
        self.available_connections = []
        self.active_connections = set()
        self.total_connections = 0
    
    def get_connection(self):
        """Get connection from pool"""
        if self.available_connections:
            conn = self.available_connections.pop()
            self.active_connections.add(conn)
            return conn
        
        if self.total_connections < self.max_connections:
            conn = self._create_connection()
            self.active_connections.add(conn)
            self.total_connections += 1
            return conn
        
        raise Exception("Connection pool exhausted")
    
    def return_connection(self, conn):
        """Return connection to pool"""
        if conn in self.active_connections:
            self.active_connections.remove(conn)
            if self._is_connection_healthy(conn):
                self.available_connections.append(conn)
            else:
                self._close_connection(conn)
                self.total_connections -= 1

Benefits:
✓ Reduced connection overhead
✓ Better resource utilization
✓ Improved performance
✓ Connection reuse`}
          </pre>

          <h3 style={styles.heading}>Caching at Load Balancer</h3>
          <p style={styles.paragraph}>
            Implement caching at the load balancer level to reduce backend load:
          </p>
          <pre style={styles.codeBlock}>
{`Load Balancer Caching:

class CachingLoadBalancer:
    def __init__(self, servers):
        self.servers = servers
        self.cache = {}  # Simple in-memory cache
        self.cache_ttl = 300  # 5 minutes
    
    def handle_request(self, request):
        cache_key = self._generate_cache_key(request)
        
        # Check cache first
        if cache_key in self.cache:
            cached_response, timestamp = self.cache[cache_key]
            if time.time() - timestamp < self.cache_ttl:
                return cached_response
            else:
                del self.cache[cache_key]
        
        # Forward to backend server
        server = self._select_server()
        response = self._forward_request(server, request)
        
        # Cache successful responses
        if self._is_cacheable(request, response):
            self.cache[cache_key] = (response, time.time())
        
        return response
    
    def _is_cacheable(self, request, response):
        """Determine if response should be cached"""
        # Cache GET requests with successful responses
        return (request.method == 'GET' and 
                response.status_code == 200 and
                'no-cache' not in response.headers.get('Cache-Control', ''))

Cacheable Content:
✓ Static assets (images, CSS, JS)
✓ API responses with low change frequency
✓ Public content
✗ User-specific content
✗ Dynamic/real-time data`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Implement comprehensive health checks:</strong> Monitor not just server availability but also 
              application health, dependency status, and performance metrics.
            </li>
            <li style={styles.listItem}>
              <strong>Use appropriate algorithms:</strong> Choose load balancing algorithms based on your application 
              characteristics and server capabilities.
            </li>
            <li style={styles.listItem}>
              <strong>Plan for failover:</strong> Implement redundant load balancers and test failover scenarios 
              regularly to ensure high availability.
            </li>
            <li style={styles.listItem}>
              <strong>Monitor performance metrics:</strong> Track response times, error rates, connection counts, 
              and server utilization to optimize load distribution.
            </li>
            <li style={styles.listItem}>
              <strong>Consider geographic distribution:</strong> Use geographically distributed load balancers 
              to reduce latency and improve user experience.
            </li>
            <li style={styles.listItem}>
              <strong>Implement graceful degradation:</strong> Design your system to handle load balancer failures 
              and partial server outages gracefully.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            Load balancing is crucial for building scalable and highly available distributed systems. The key is 
            choosing the right type of load balancer, appropriate algorithms, and implementing proper health monitoring 
            and failover mechanisms.
          </p>
          <p style={styles.paragraph}>
            Remember that load balancing involves trade-offs between simplicity, performance, and fault tolerance. 
            Design your load balancing strategy based on your specific requirements for scalability, availability, 
            and consistency, and always test your configuration under realistic load conditions.
          </p>
        </section>
      </main>
    </article>
  );
};

export default LoadBalancing;
